"use client"

import { useMemo, useState, useEffect, useRef } from "react"
import { format } from "date-fns"
import { Clock, ChevronLeft, Check, Loader2, Globe, Instagram, Facebook, Phone, MessageSquare } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { AuthModal } from "@/components/auth/auth-modal"
import { BookingTimePicker } from "./booking-time-picker"
import dynamic from "next/dynamic"
// Lazy — keeps @stripe/* + js.stripe.com out of the public barber-profile
// bundle until the client reaches the payment step.
const StripeCheckout = dynamic(() => import("./stripe-checkout").then((m) => m.StripeCheckout), { ssr: false })
import { useUserRole } from "@/providers/user-role-provider"
import { createClient } from "@/lib/supabase/client"
import { computeProcessing } from "@/lib/booking-fees"
import { SmsConsent } from "@/components/sms-consent"
import { SOCIAL_ICON } from "@/components/icons/social"

interface Service {
  id: string
  name: string
  description?: string | null
  duration_minutes: number
  price_cents: number
  currency?: string | null
  deposit_cents?: number | null
}

interface Social {
  url: string
  label: string
}

const GREEN = "#10b981"
const STEPS = ["service", "time", "details"] as const
type Step = (typeof STEPS)[number]
const STEP_LABEL: Record<Step, string> = { service: "Service", time: "Date & time", details: "Your details" }

// Brand social glyphs lucide doesn't carry cleanly.


// Clean first+last casing for a typed-in client name (e.g. "john stamos" → "John Stamos").
function titleCase(s: string) {
  return s
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

function money(cents: number, cur = "usd") {
  if (cents <= 0) return "—"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: cur.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

// Client-facing booking flow: service → date/time → name+phone (guest) → done.
// Stepped, create-shop style. The profile header sits above the flow and is
// hidden on the confirmation screen, where it's echoed inside the card.
export function StorefrontBooking({
  barberId,
  barberName,
  avatarUrl,
  barberPhone,
  bio,
  socials = [],
  services,
  onBooked,
  hideHeader = false,
  acceptsOnlinePayments = false,
  depositsEnabled = false,
  passProcessingToClient = false,
}: {
  barberId: string
  barberName: string
  avatarUrl?: string
  barberPhone?: string
  bio?: string | null
  socials?: Social[]
  services: Service[]
  /** Fires once a booking is confirmed — lobby/kiosk uses it to auto-reset. */
  onBooked?: () => void
  /** When the page already renders a rich profile header (booking lens), skip
      the built-in avatar/name/bio/socials header so it isn't duplicated. */
  hideHeader?: boolean
  /** Whether the barber can take payment online (Connect-connected, or test
      mode). When false, online deposit/prepay is hidden — booking is in-person
      only — so money never routes to a platform balance in production. */
  acceptsOnlinePayments?: boolean
  /** Barber collects a deposit up front. When false, per-service deposit
      amounts are ignored and the deposit pay option never shows. */
  depositsEnabled?: boolean
  /** Barber passes the card-processing cost to the client — shown as a separate
      "Processing" line added to any card payment, sized so the barber still nets
      their full listed price. Must mirror the server (deposit-intent), which is
      authoritative; both use lib/booking-fees. */
  passProcessingToClient?: boolean
}) {
  // A logged-in viewer (a client booking from their own profile lens) already
  // has an account — the "Create an account" nudge is for guest bookers only.
  const { userId } = useUserRole()
  const sorted = useMemo(() => [...services].sort((a, b) => b.price_cents - a.price_cents), [services])

  const [step, setStep] = useState<Step>("service")
  const [done, setDone] = useState(false)
  const [service, setService] = useState<Service | null>(null)
  const [iso, setIso] = useState<string | null>(null)
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authOpen, setAuthOpen] = useState(false)
  // Deposit payment: once a clientSecret is set, the details step swaps to the
  // Stripe Payment Element; on success we book with the verified payment intent.
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [custSession, setCustSession] = useState<string | null>(null)
  const [depositPi, setDepositPi] = useState<string | null>(null)
  // How the client pays:
  //   "in_person" — pay nothing now, settle at the shop (cash or card)
  //   "deposit"   — card now for the service's deposit, rest in person
  //   "full"      — prepay the whole service by card now
  // Default is set per-service on selection: deposit services default to
  // "deposit" (the barber asked for one); others default to "in_person".
  const [payType, setPayType] = useState<"in_person" | "deposit" | "full">("in_person")

  // Logged-in clients shouldn't re-key their details — prefill name + phone from
  // their account so they just review and confirm. Only fills empty fields, so
  // anything they type is preserved.
  useEffect(() => {
    if (!userId) return
    const supabase = createClient()
    ;(async () => {
      const { data } = await supabase
        .from("clients")
        .select("name, phone, email")
        .eq("auth_user_id", userId)
        .maybeSingle()
      if (data?.name) setName((n) => n || data.name!)
      if (data?.phone) setPhone((p) => p || data.phone!)
      // Email: prefer the clients row, fall back to the auth account email so a
      // logged-in client never has to re-type it (seamless checkout).
      const { data: { user } } = await supabase.auth.getUser()
      const em = (data as { email?: string | null } | null)?.email || user?.email || ""
      if (em) setEmail((e) => e || em)
    })()
  }, [userId])

  const idx = STEPS.indexOf(step)

  // Keep the booking flow in view across step changes. The time step (calendar)
  // is tall; when it collapses to the shorter details step the page reflowed
  // and left the viewport parked on the portfolio BELOW the flow. On every step
  // change past the first, pull the flow header back to the top of the viewport.
  const flowRef = useRef<HTMLDivElement>(null)
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    // Double rAF so the layout has fully reflowed to the new (shorter) step
    // before we measure — a single frame can fire mid-reflow and scroll against
    // stale layout, which reads as a jump. `block: "nearest"` scrolls the
    // minimum needed: if the flow header is already visible (common on desktop /
    // smaller viewports), it doesn't move at all, so there's no jump — it only
    // pulls the flow up when a reflow actually parked the viewport below it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        flowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
      })
    })
  }, [step])

  function goTo(target: Step) {
    setError(null)
    // Any navigation invalidates a started payment — a PaymentIntent is bound to
    // a specific service+amount, so leaving the payment view must drop it (else
    // a service-switch could charge the OLD service's deposit). The Payment
    // Element is re-created fresh when the client re-reaches it.
    setClientSecret(null)
    setDepositPi(null)
    setCustSession(null)
    setStep(target)
  }
  function goBack() {
    if (idx > 0) goTo(STEPS[idx - 1])
  }

  // Deposit only counts when the barber has deposits enabled.
  const depositCents = depositsEnabled ? (service?.deposit_cents ?? 0) : 0
  // Base for the current card payment (deposit vs full), and the client-borne
  // processing surcharge on it (0 unless the barber passes it through). Mirrors
  // the server so the shown total matches the charge.
  const payBaseCents = payType === "full" ? (service?.price_cents ?? 0) : depositCents
  // Same gross-up the server uses (lib/booking-fees) so the shown total can
  // never drift from the amount actually charged.
  const payProcessing = computeProcessing(payBaseCents, passProcessingToClient && payType !== "in_person")
  const surchargeCents = payProcessing.processingAmountCents
  const payTotalCents = payProcessing.totalCents

  // Guards a single booking submission so a double-fired Stripe onSuccess (or a
  // re-tapped Pay button) can't create two appointments / duplicate writes.
  const bookingRef = useRef(false)
  // Server-verified receipt (what was actually captured), set on a successful
  // book — the done screen renders from this, not from drifting client state.
  const [paidReceipt, setPaidReceipt] = useState<{ type: "deposit" | "full" | null; amount_cents: number; price_cents: number } | null>(null)

  // Route by pay choice: "in_person" books with no charge; "deposit"/"full"
  // open the card step (Payment Element) for the matching amount.
  async function proceed() {
    if (submitting) return // re-entrancy guard (button disable is not enough)
    if (!service || !iso) return
    if (!name.trim() || (!userId && phone.replace(/[^\d]/g, "").length < 7)) {
      setError(userId ? "Enter your name" : "Enter your name and a valid phone number")
      return
    }
    if (payType === "in_person") { await confirm(); return }
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch("/api/appointments/deposit-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ barber_id: barberId, service_id: service.id, start_at: iso, pay_type: payType }),
      })
      const json = await res.json()
      if (!res.ok) { setError(json.error || "Couldn't start the payment"); return }
      if (json.connectRequired) {
        // The barber can't take payment online yet — switch to in-person and tell
        // the client instead of silently charging nothing.
        setPayType("in_person")
        setError("This barber isn't set up for online payment yet — you can book now and pay in person.")
        return
      }
      if (json.amount > 0 && json.clientSecret) {
        setDepositPi(json.paymentIntentId)
        setCustSession(json.customerSessionClientSecret ?? null) // shows saved cards
        setClientSecret(json.clientSecret) // swaps the details card to the Payment Element
      } else {
        await confirm() // nothing to charge (e.g. $0 service) — just book
      }
    } catch {
      setError("Couldn't start the payment")
    } finally {
      setSubmitting(false)
    }
  }

  async function confirm(paymentIntentId?: string) {
    if (bookingRef.current || done) return // single-submit guard (Stripe can fire onSuccess twice)
    if (!service || !iso) return
    if (!name.trim() || (!userId && phone.replace(/[^\d]/g, "").length < 7)) {
      setError(userId ? "Enter your name" : "Enter your name and a valid phone number")
      return
    }
    bookingRef.current = true
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch("/api/appointments/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          barber_id: barberId,
          service_id: service.id,
          start_at: iso,
          client_name: name,
          client_phone: phone,
          client_email: email,
          payment_intent_id: paymentIntentId ?? null,
          // Booker's timezone — stored on the appointment so confirmation /
          // reschedule / cancel emails render the time in THEIR local time.
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Channel captured on page load (?src= / referrer), stored in a cookie
          // by <BookingViewTracker/> — attributes this booking to its source.
          booking_channel: document.cookie.match(/(?:^|;\s*)booth_channel=([^;]+)/)?.[1] || "direct",
        }),
      })
      const json = await res.json()
      if (!res.ok) {
        setError(json.error || "Couldn't book")
        if (res.status === 409) goTo("time")
        return
      }
      if (json.payment) setPaidReceipt(json.payment) // server-verified receipt
      setDone(true)
      onBooked?.()
    } catch {
      setError("Couldn't book")
    } finally {
      bookingRef.current = false
      setSubmitting(false)
    }
  }

  // ---- confirmation: no top header; profile echoed in-card, gentle entrance --
  if (done) {
    return (
      <>
        <div className="animate-fade-in rounded-[2rem] bg-white bk-glass shadow-sm px-7 pt-10 pb-8 text-center">
          <Avatar className="w-16 h-16 mx-auto">
            <AvatarImage src={avatarUrl || ""} alt={barberName} />
            <AvatarFallback className="bg-muted">{barberName.charAt(0)}</AvatarFallback>
          </Avatar>
          {name.trim() && <p className="mt-3 text-base font-normal">{titleCase(name)}</p>}
          <Check
            className="mx-auto mt-3 h-9 w-9 animate-zoom-in [animation-delay:140ms] [animation-duration:400ms] [animation-fill-mode:both]"
            strokeWidth={2.5}
            style={{ color: GREEN }}
          />
          <h3 className="mt-2 text-lg font-semibold">You&apos;re booked</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {service?.name} with {barberName}
          </p>
          {iso && <p className="font-mono text-sm mt-2">{format(new Date(iso), "EEE, MMM d · h:mm a")}</p>}

          {/* Receipt — rendered from the SERVER-verified payment (paidReceipt)
              when available, so the amounts reflect what was actually captured
              rather than client-side pay-choice state that may have drifted. */}
          {service && (service.price_cents ?? 0) > 0 && (() => {
            const cur = service.currency || "usd"
            const priceCents = paidReceipt?.price_cents ?? service.price_cents ?? 0
            const effType = paidReceipt ? paidReceipt.type : (payType === "in_person" ? null : payType)
            const paidNow = paidReceipt ? paidReceipt.amount_cents : (payType === "full" ? priceCents : payType === "deposit" ? depositCents : 0)
            // The captured amount may include a client-paid processing surcharge.
            // Split it out so the "toward service" balance stays correct — the
            // surcharge covers the barber's card cost, not the service price.
            const basePaid = effType === "full" ? priceCents : effType === "deposit" ? depositCents : 0
            const processingPaid = Math.max(paidNow - basePaid, 0)
            const dueAtShop = Math.max(priceCents - basePaid, 0)
            return (
              <div className="mt-5 mx-auto max-w-[280px] rounded-2xl bg-muted/60 p-4 text-left">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{service.name}</span>
                  <span className="tabular-nums">{money(priceCents, cur)}</span>
                </div>
                {basePaid > 0 && (
                  <div className="flex justify-between text-xs mt-1.5">
                    <span className="text-muted-foreground">Paid now{effType === "deposit" ? " · deposit" : ""}</span>
                    <span className="tabular-nums" style={{ color: GREEN }}>−{money(basePaid, cur)}</span>
                  </div>
                )}
                {processingPaid > 0 && (
                  <div className="flex justify-between text-xs mt-1.5">
                    <span className="text-muted-foreground">Processing</span>
                    <span className="tabular-nums" style={{ color: GREEN }}>−{money(processingPaid, cur)}</span>
                  </div>
                )}
                {dueAtShop > 0 ? (
                  // Soft estimate — the barber sets the final in-shop total.
                  <div className="mt-2 pt-2 border-t border-border flex justify-between text-xs text-muted-foreground">
                    <span>Remaining at your appointment</span>
                    <span className="tabular-nums">≈ {money(dueAtShop, cur)}</span>
                  </div>
                ) : (
                  <div className="mt-2 pt-2 border-t border-border flex justify-between text-sm font-semibold">
                    <span>Paid in full</span>
                    <span className="tabular-nums">{money(priceCents, cur)}</span>
                  </div>
                )}
              </div>
            )
          })()}

          <p className="text-xs text-muted-foreground mt-3">
            {userId ? "It’s on your dashboard. A confirmation is on the way." : "We saved your number. A confirmation is on the way."}
          </p>

          {/* Reach the barber — quick call/text after booking. */}
          {barberPhone && (
            <div className="mt-5">
              <p className="text-[11px] text-muted-foreground mb-2">Need to reach {barberName.split(" ")[0]}?</p>
              <div className="flex items-center justify-center gap-2">
                <a
                  href={`tel:${barberPhone.replace(/[^\d+]/g, "")}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3.5 py-1.5 text-xs font-medium hover:bg-muted/70 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" /> Call
                </a>
                <a
                  href={`sms:${barberPhone.replace(/[^\d+]/g, "")}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3.5 py-1.5 text-xs font-medium hover:bg-muted/70 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Text
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Optional account — guest bookers only. A signed-in client already
            has an account, so the nudge is hidden for them. */}
        {!userId && (
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground mb-2.5">New here? Save your appointments to an account.</p>
            <Button
              size="sm"
              onClick={() => setAuthOpen(true)}
              className="rounded-full bg-black text-white font-light shadow-none hover:bg-black/90"
            >
              Create an account
            </Button>
          </div>
        )}
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="signup" />
      </>
    )
  }

  return (
    <>
      {/* Profile header — hidden once booked, or when the page renders its own
          rich header (booking lens). */}
      {!hideHeader && (
        <div className="mb-8 flex flex-col items-center text-center">
          <Avatar className="shrink-0" style={{ width: 64, height: 64 }}>
            <AvatarImage src={avatarUrl || ""} alt={barberName} />
            <AvatarFallback className="text-xl bg-muted">{barberName.charAt(0)}</AvatarFallback>
          </Avatar>
          <h1 className="mt-3 text-2xl font-semibold">{barberName}</h1>
          {bio && <p className="mt-1 text-sm text-muted-foreground max-w-sm">{bio}</p>}
          {socials.length > 0 && (
            <div className="mt-3 flex items-center justify-center gap-4">
              {socials.map(({ url, label }) => {
                const Icon = SOCIAL_ICON[label] ?? Globe
                return (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="text-foreground hover:text-muted-foreground hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </a>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* scroll-mt keeps the flow clear of the top chrome when we scroll it
          into view on a step change. */}
      <div ref={flowRef} className="space-y-4 scroll-mt-20">
        {/* progress + back */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            {idx > 0 ? (
              <button
                onClick={goBack}
                className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            ) : (
              <span />
            )}
            <span className="text-xs text-muted-foreground">
              Step {idx + 1} of {STEPS.length} · {STEP_LABEL[step]}
            </span>
          </div>
          <div className="flex gap-1.5">
            {STEPS.map((s, i) => {
              const reachable = i <= idx
              return (
                <button
                  key={s}
                  disabled={!reachable}
                  onClick={() => reachable && goTo(s)}
                  className="h-1 flex-1 overflow-hidden rounded-full bg-muted disabled:cursor-default"
                  aria-label={STEP_LABEL[s]}
                >
                  <span
                    className="block h-full rounded-full transition-all duration-500"
                    style={{ width: i <= idx ? "100%" : "0%", backgroundColor: GREEN }}
                  />
                </button>
              )
            })}
          </div>
        </div>

        {/* selection summary chip — hidden on the service step (you're picking
            there; the selected card is highlighted instead) */}
        {service && step !== "service" && (
          <div className="rounded-xl bg-muted/40 bk-glass p-3 flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{service.name}</p>
              <p className="text-xs text-muted-foreground font-mono">
                {service.duration_minutes} min{iso ? ` · ${format(new Date(iso), "EEE MMM d, h:mm a")}` : ""}
              </p>
            </div>
            <p className="font-mono flex-shrink-0" style={{ color: GREEN }}>
              {money(service.price_cents, service.currency || "usd")}
            </p>
          </div>
        )}

        {/* step content — re-keyed so it slides in on every change */}
        <div key={step} className="animate-slide-in-from-bottom">
          {step === "service" && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Choose a service</p>
              {sorted.length === 0 ? (
                <p className="text-sm text-muted-foreground">No services listed yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2.5">
                  {sorted.map((s) => {
                    const isSel = service?.id === s.id
                    return (
                    <button
                      key={s.id}
                      onClick={() => {
                        setService(s)
                        setIso(null) // force a fresh time pick for the new service's duration
                        // Default pay choice: deposit services start on "deposit",
                        // everything else starts on "pay in person". If the barber
                        // can't take online payment, it's always in-person.
                        setPayType(acceptsOnlinePayments && depositsEnabled && (s.deposit_cents ?? 0) > 0 ? "deposit" : "in_person")
                        goTo("time")
                      }}
                      // Unselected = no container (blends with the page); only the
                      // SELECTED card gets a dark fill so it stands alone.
                      className={`text-left rounded-3xl p-4 transition-all hover:-translate-y-0.5 ${
                        isSel ? "bg-foreground shadow-sm" : "bg-transparent"
                      }`}
                    >
                      <p className={`text-sm font-medium truncate leading-tight ${isSel ? "text-background" : "text-foreground"}`}>{s.name}</p>
                      {s.description && <p className={`text-xs mt-1 line-clamp-2 ${isSel ? "text-background/60" : "text-muted-foreground"}`}>{s.description}</p>}
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs font-mono flex items-center gap-1 ${isSel ? "text-background/50" : "text-muted-foreground/70"}`}>
                          <Clock className="w-3 h-3" />
                          {s.duration_minutes} min
                        </span>
                        <span className="font-mono text-sm" style={{ color: GREEN }}>
                          {money(s.price_cents, s.currency || "usd")}
                        </span>
                      </div>
                    </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {step === "time" && service && (
            <BookingTimePicker
              barberId={barberId}
              durationMin={service.duration_minutes}
              onSelect={(i) => {
                setIso(i)
                goTo("details")
              }}
            />
          )}

          {step === "details" && service && iso && !clientSecret && (
            <div className="rounded-[2rem] bg-white bk-glass shadow-sm p-5 space-y-3">
              <p className="text-sm font-medium">Your details</p>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl" />
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                type="tel"
                inputMode="tel"
                className="rounded-xl"
              />
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (for your confirmation)"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                className="rounded-xl"
              />
              {/* Confirmations are emailed (Resend). Phone stays for the shop's
                  records + future texts once SMS is enabled. */}
              <p className="text-[10px] leading-snug text-muted-foreground">
                We'll email your booking confirmation and any updates.
              </p>
              <SmsConsent />
              {/* How-to-pay choice. Deposit services → Deposit vs. Pay in full.
                  Others → Pay in person vs. Pay in full. Free ($0) services and
                  barbers who can't take payment online show no choice (in-person). */}
              {acceptsOnlinePayments && (service.price_cents ?? 0) > 0 && (() => {
                const cur = service.currency || "usd"
                const left = depositCents > 0
                  ? { key: "deposit" as const, title: "Pay deposit", amount: money(depositCents, cur), note: "rest at the shop · cash or card" }
                  : { key: "in_person" as const, title: "Pay in person", amount: "$0 now", note: "cash or card at the shop" }
                const right = { key: "full" as const, title: "Pay in full", amount: money(service.price_cents, cur), note: "all set, nothing owed" }
                return (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {[left, right].map((opt) => {
                      const sel = payType === opt.key
                      // Selection inverts the card: page-coloured text on a
                      // foreground fill. It used to be bg-black + white text,
                      // which read fine when this page was light — but the
                      // booking storefront is force-dark for every visitor now,
                      // so "selected" was black on near-black and you couldn't
                      // tell which option you'd picked. Using the foreground/
                      // background tokens keeps the inversion correct whichever
                      // surface this renders on.
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          aria-pressed={sel}
                          onClick={() => setPayType(opt.key)}
                          className={`rounded-2xl p-3 text-left transition-colors ${
                            sel
                              ? "bg-foreground"
                              : "bg-foreground/[0.06] hover:bg-foreground/[0.12]"
                          }`}
                        >
                          <p className={`text-[13px] font-semibold ${sel ? "text-background" : ""}`}>{opt.title}</p>
                          <p className={`text-base font-bold leading-tight ${sel ? "text-background" : ""}`}>{opt.amount}</p>
                          <p className={`text-[11px] ${sel ? "text-background/70" : "text-muted-foreground"}`}>{opt.note}</p>
                        </button>
                      )
                    })}
                  </div>
                )
              })()}
              {/* Processing line — only when the barber passes the card cost to
                  the client AND they're paying by card. Mirrors the server
                  (lib/booking-fees) so this total equals what's charged. */}
              {surchargeCents > 0 && (
                <div className="rounded-2xl bg-muted/50 p-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{payType === "full" ? "Service" : "Deposit"}</span>
                    <span className="tabular-nums">{money(payBaseCents, service.currency || "usd")}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Processing</span>
                    <span className="tabular-nums">+{money(surchargeCents, service.currency || "usd")}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-1 mt-1 border-t border-border">
                    <span>Total</span>
                    <span className="tabular-nums">{money(payTotalCents, service.currency || "usd")}</span>
                  </div>
                </div>
              )}
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button
                onClick={proceed}
                disabled={submitting}
                className="w-full h-11 rounded-full bg-black text-white hover:bg-black/90 font-semibold"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {payType === "in_person"
                  ? "Confirm booking"
                  : `Continue · ${money(payTotalCents, service.currency || "usd")}`}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                {payType === "full"
                  ? "Prepay the full amount now — nothing to pay at the shop."
                  : payType === "deposit"
                    ? `A ${money(depositCents, service.currency || "usd")} deposit confirms your spot — pay the rest at the shop, cash or card.`
                    : userId ? "Confirming with your saved details — edit if anything's changed." : "No account needed. Just your name and number."}
              </p>
            </div>
          )}

          {step === "details" && service && iso && clientSecret && (
            // Payment card. Stripe's Payment Element is themed to match (light
            // "stripe" theme on a white card; "night" theme on a dark card) by
            // StripeCheckout, which detects the effective surface — so this
            // panel and the embedded form always agree. No bk-glass here (its
            // dark tint would fight the solid surface + hide Stripe's labels).
            <div className="rounded-[2rem] bg-white text-neutral-900 dark:bg-[#202020] dark:text-white shadow-md p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-neutral-900 dark:text-white">{payType === "full" ? "Pay in full" : "Pay your deposit"}</p>
                <button
                  type="button"
                  onClick={() => { setClientSecret(null); setDepositPi(null); setError(null) }}
                  className="text-xs text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                >
                  Back
                </button>
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
              <StripeCheckout
                clientSecret={clientSecret}
                customerSessionClientSecret={custSession}
                amount={payTotalCents / 100}
                onSuccess={() => confirm(depositPi || undefined)}
                onError={(msg) => setError(msg)}
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 text-center">
                {surchargeCents > 0
                  ? payType === "full"
                    ? `${money(payTotalCents, service.currency || "usd")} now (incl. ${money(surchargeCents, service.currency || "usd")} processing) — nothing to pay at the shop.`
                    : `${money(payTotalCents, service.currency || "usd")} now (incl. ${money(surchargeCents, service.currency || "usd")} processing) · pay the rest at the shop, cash or card.`
                  : payType === "full"
                    ? `${money(service.price_cents, service.currency || "usd")} now — nothing to pay at the shop.`
                    : `${money(depositCents, service.currency || "usd")} deposit now · pay the rest at the shop, cash or card.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
